/* Mats · Página de colección (sections/mats-collection.liquid)
   - Filtros, orden y corte sin recargar (Section Rendering API: ?section_id=)
   - «Ver más» y, tras el primer clic, carga al bajar
   - Panel de filtros (dialog), guía de cortes, columnas recordadas, entrada escalonada
   Usa window.MatsWishlist (mats-product-card.js) para refrescar los «Guardar». */
(() => {
  if (customElements.get('mats-collection')) return;

  const DENSITY_KEY = 'mats:cl:density';
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* modo privado */ } },
  };

  class MatsCollection extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.classList.add('mb-cl--js');
      this.sectionId = this.dataset.section;
      this.drawer = this.querySelector('[data-mc-drawer]');
      this.cutsDialog = this.querySelector('[data-mc-cuts]');
      this.infinite = this.dataset.infinite === 'true';
      this.autoLoad = false;
      this.reqId = 0;

      this.bindClicks();
      this.bindDrawer();
      this.bindSort();
      this.bindDensity();
      this.bindPrice();
      this.reveal();
      this.bindStick();
      window.addEventListener('popstate', () => this.load(window.location.href, { push: false }));
    }

    /* ---------------- Navegación ---------------- */
    bindClicks() {
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-mc-link]');
        if (link && this.contains(link)) {
          e.preventDefault();
          this.load(link.href);
          return;
        }
        const cut = e.target.closest('[data-mc-cut]');
        if (cut && this.contains(cut)) {
          e.preventDefault();
          const url = new URL(window.location.href);
          url.searchParams.delete(cut.dataset.mcCutParam);
          url.searchParams.delete('page');
          if (cut.dataset.mcCut && !cut.classList.contains('is-active')) url.searchParams.append(cut.dataset.mcCutParam, cut.dataset.mcCut);
          this.load(url.toString());
          return;
        }
        const next = e.target.closest('[data-mc-next]');
        if (next && this.contains(next)) {
          e.preventDefault();
          this.loadMore(next);
          if (this.infinite) this.autoLoad = true;
          return;
        }
        if (e.target.closest('[data-mc-open]')) { this.openDialog(this.drawer); return; }
        if (e.target.closest('[data-mc-close]')) { this.closeDialog(this.drawer); return; }
        if (e.target.closest('[data-mc-cuts-open]')) { this.openDialog(this.cutsDialog); return; }
        if (e.target.closest('[data-mc-cuts-close]')) { this.closeDialog(this.cutsDialog); }
      });
    }

    sectionUrl(href) {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      return url.toString();
    }

    cleanUrl(href) {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('section_id');
      url.searchParams.delete('page');
      return url.pathname + (url.search ? url.search : '');
    }

    async fetchDoc(href) {
      const res = await fetch(this.sectionUrl(href), { credentials: 'same-origin' });
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    async load(href, { push = true } = {}) {
      const id = ++this.reqId;
      this.classList.add('is-loading');
      try {
        const doc = await this.fetchDoc(href);
        if (id !== this.reqId) return;
        this.swap(doc);
        if (push) window.history.pushState({ mats: true }, '', this.cleanUrl(href));
        this.autoLoad = false;
        const bar = this.querySelector('[data-mc-bar]');
        if (bar && !this.drawer?.open) {
          const top = bar.getBoundingClientRect().top;
          if (top < -2 || top > window.innerHeight * 0.6) {
            const y = window.scrollY + this.getBoundingClientRect().top - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height')) || 0);
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }
      } catch (err) {
        window.location.href = this.cleanUrl(href);
      } finally {
        if (id === this.reqId) this.classList.remove('is-loading');
      }
    }

    swap(doc) {
      const scroll = this.querySelector('.mb-cl-drawer__scroll');
      const scrollTop = scroll ? scroll.scrollTop : 0;
      doc.querySelectorAll('[data-mc-swap]').forEach((fresh) => {
        const key = fresh.dataset.mcSwap;
        const old = this.querySelector(`[data-mc-swap="${key}"]`);
        if (!old) return;
        old.replaceWith(fresh);
        if (key === 'fcount') fresh.hidden = fresh.textContent.trim() === '0';
      });
      const newScroll = this.querySelector('.mb-cl-drawer__scroll');
      if (newScroll) newScroll.scrollTop = scrollTop;
      const sortInput = this.querySelector('[data-mc-sort-input]');
      const freshSort = doc.querySelector('[data-mc-sort-input]');
      if (sortInput && freshSort) sortInput.value = freshSort.value;
      const freshList = doc.querySelector('[data-mc-sort-list]');
      const list = this.querySelector('[data-mc-sort-list]');
      if (freshList && list) list.innerHTML = freshList.innerHTML;
      this.bindPrice();
      this.reveal();
      window.MatsWishlist?.sync(this);
    }

    /* ---------------- Barra pegada (cápsula) ---------------- */
    bindStick() {
      const bar = this.querySelector('[data-mc-bar]');
      if (!bar) return;
      let raf = 0;
      const check = () => {
        raf = 0;
        const top = parseFloat(getComputedStyle(bar).top) || 0;
        const r = bar.getBoundingClientRect();
        const stuck = r.top <= top + 1 && this.getBoundingClientRect().top < top - 1 && this.getBoundingClientRect().bottom > top + r.height;
        bar.classList.toggle('is-stuck', stuck);
      };
      const onScroll = () => { if (!raf) raf = requestAnimationFrame(check); };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      check();
    }

    /* ---------------- Ver más ---------------- */
    async loadMore(btn) {
      if (this._moreBusy || !btn) return;
      this._moreBusy = true;
      btn.classList.add('is-loading');
      try {
        const doc = await this.fetchDoc(btn.href);
        const items = doc.querySelectorAll('[data-mc-grid] > li');
        const grid = this.querySelector('[data-mc-grid]');
        items.forEach((li, i) => { li.style.setProperty('--i', i % 8); grid?.appendChild(li); });
        const more = doc.querySelector('[data-mc-swap="more"]');
        const oldMore = this.querySelector('[data-mc-swap="more"]');
        if (more && oldMore) oldMore.replaceWith(more);
        this.reveal();
        window.MatsWishlist?.sync(this);
        this.watchMore();
      } catch (err) {
        window.location.href = btn.href;
      } finally {
        this._moreBusy = false;
      }
    }

    watchMore() {
      this._moreIO?.disconnect();
      if (!this.autoLoad) return;
      const btn = this.querySelector('[data-mc-next]');
      if (!btn) return;
      this._moreIO = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) this.loadMore(this.querySelector('[data-mc-next]'));
      }, { rootMargin: '0px 0px 600px 0px' });
      this._moreIO.observe(btn);
    }

    /* ---------------- Entrada escalonada ---------------- */
    reveal() {
      const items = Array.from(this.querySelectorAll('.mb-cl__item:not(.is-in)'));
      if (!items.length) return;
      if (!('IntersectionObserver' in window)) { items.forEach((el) => el.classList.add('is-in')); return; }
      this._io = this._io || new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); this._io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -40px 0px' });
      items.forEach((el) => this._io.observe(el));
    }

    /* ---------------- Paneles ---------------- */
    openDialog(dlg) {
      if (!dlg) return;
      if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      if (!dlg._bound) {
        dlg._bound = true;
        dlg.addEventListener('click', (e) => { if (e.target === dlg) this.closeDialog(dlg); });
        dlg.addEventListener('close', () => { document.documentElement.style.overflow = ''; });
      }
    }

    closeDialog(dlg) {
      if (!dlg) return;
      if (typeof dlg.close === 'function') dlg.close(); else dlg.removeAttribute('open');
      document.documentElement.style.overflow = '';
    }

    bindDrawer() {
      const form = this.querySelector('[data-mc-form]');
      if (!form) return;
      form.addEventListener('submit', (e) => { e.preventDefault(); this.applyForm(); });
      form.addEventListener('change', (e) => {
        if (e.target.matches('[data-mc-price-range]')) return; // se aplica al soltar (ver bindPrice)
        clearTimeout(this._formT);
        this._formT = setTimeout(() => this.applyForm(), e.target.matches('[data-mc-price-input]') ? 0 : 120);
      });
    }

    applyForm() {
      const form = this.querySelector('[data-mc-form]');
      if (!form) return;
      const params = new URLSearchParams();
      new FormData(form).forEach((v, k) => {
        if (v === '' || v === null) return;
        params.append(k, v);
      });
      // El precio llega en euros enteros: Shopify lo espera igual (filter.v.price.gte=20)
      const url = `${this.dataset.url}${params.toString() ? `?${params}` : ''}`;
      this.load(url);
    }

    /* ---------------- Precio ---------------- */
    bindPrice() {
      const box = this.querySelector('[data-mc-price]');
      if (!box || box._bound) return;
      box._bound = true;
      const max = Number(box.dataset.max) || 100;
      const rMin = box.querySelector('[data-mc-price-range="min"]');
      const rMax = box.querySelector('[data-mc-price-range="max"]');
      const iMin = box.querySelector('[data-mc-price-input="min"]');
      const iMax = box.querySelector('[data-mc-price-input="max"]');
      const fill = box.querySelector('[data-mc-price-fill]');
      const paint = () => {
        const lo = Math.min(Number(rMin.value), Number(rMax.value));
        const hi = Math.max(Number(rMin.value), Number(rMax.value));
        fill.style.setProperty('--lo', `${(lo / max) * 100}%`);
        fill.style.setProperty('--hi', `${(hi / max) * 100}%`);
        return [lo, hi];
      };
      const fromRange = () => {
        const [lo, hi] = paint();
        iMin.value = lo > 0 ? lo : '';
        iMax.value = hi < max ? hi : '';
      };
      [rMin, rMax].forEach((r) => {
        r.addEventListener('input', fromRange);
        r.addEventListener('change', () => { fromRange(); this.applyForm(); });
      });
      [iMin, iMax].forEach((inp) => inp.addEventListener('input', () => {
        rMin.value = iMin.value || 0;
        rMax.value = iMax.value || max;
        paint();
      }));
      paint();
    }

    /* ---------------- Ordenar ---------------- */
    bindSort() {
      const wrap = this.querySelector('[data-mc-sort]');
      if (!wrap) return;
      const btn = wrap.querySelector('[data-mc-sort-btn]');
      const list = () => wrap.querySelector('[data-mc-sort-list]');
      const close = () => { list().hidden = true; btn.setAttribute('aria-expanded', 'false'); };
      const open = () => {
        list().hidden = false;
        btn.setAttribute('aria-expanded', 'true');
        (list().querySelector('[aria-selected="true"]') || list().querySelector('li'))?.focus();
      };
      btn.addEventListener('click', () => (list().hidden ? open() : close()));
      document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) close(); });
      wrap.addEventListener('keydown', (e) => {
        const items = Array.from(list().querySelectorAll('li'));
        const i = items.indexOf(document.activeElement);
        if (e.key === 'Escape') { close(); btn.focus(); }
        if (e.key === 'ArrowDown') { e.preventDefault(); (items[i + 1] || items[0])?.focus(); }
        if (e.key === 'ArrowUp') { e.preventDefault(); (items[i - 1] || items[items.length - 1])?.focus(); }
        if ((e.key === 'Enter' || e.key === ' ') && i > -1) { e.preventDefault(); items[i].click(); }
      });
      wrap.addEventListener('click', (e) => {
        const li = e.target.closest('li[data-value]');
        if (!li) return;
        close();
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', li.dataset.value);
        url.searchParams.delete('page');
        this.load(url.toString());
      });
    }

    /* ---------------- Columnas ---------------- */
    bindDensity() {
      const btns = this.querySelectorAll('[data-mc-density]');
      if (!btns.length) return;
      const set = (n, save) => {
        if (n) this.dataset.density = n; else delete this.dataset.density;
        const current = n || getComputedStyle(this).getPropertyValue('--cl-cols').trim();
        btns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mcDensity === current)));
        if (save && n) store.set(DENSITY_KEY, n);
      };
      set(store.get(DENSITY_KEY), false);
      btns.forEach((b) => b.addEventListener('click', () => set(b.dataset.mcDensity, true)));
    }
  }

  customElements.define('mats-collection', MatsCollection);
})();
